/**
 * El ruido: el tono, el pop-up del sistema y las preferencias de cada uno.
 *
 * Vive aparte de `avisos.ts` porque nada de esto se puede probar sin un
 * navegador —`AudioContext` y `Notification` no existen en Node— y mezclarlo
 * con las reglas dejaría sin test la parte que sí se puede verificar.
 *
 * Reemplaza a `components/AlertaDeMensaje.ts` y a la mitad sonora del
 * `avisos.ts` anterior: eran DOS tonos distintos con DOS interruptores y dos
 * claves de `localStorage` —la campana del header y el parlante del chat—, y
 * podían quedar uno prendido y otro apagado. Ahora hay un tono y una
 * preferencia.
 *
 * LAS DOS TRABAS DEL NAVEGADOR NO SON LA MISMA
 *
 *   · **El sonido no pide permiso.** Solo exige que la persona haya tocado la
 *     página —cualquier clic—. Por eso `desbloquearSonido` se engancha al
 *     primer gesto y nadie tiene que hacer un trámite.
 *   · **El pop-up sí pide permiso**, con ventana del navegador, y eso no se
 *     puede saltear desde el código. Se pide solo desde un clic: una ventana de
 *     permiso que aparece sin que nadie la pidiera es lo que hace que la gente
 *     apriete «Bloquear» por reflejo, y ahí se pierde para siempre.
 *
 * Si algo de esto falla, falla en silencio y el portal sigue: el número de la
 * pestaña no depende de nada de acá.
 */
import { ICONO_DEL_AVISO, type OpcionesDelPopup, type PermisoPopup, sinBotones } from './avisos'

// ── Preferencias ──────────────────────────────────────────────────────────

const CLAVE_SONIDO = 'pgh.avisos.sonido'
const CLAVE_POPUP = 'pgh.avisos.popup'
/** La de la campana anterior del header: `'1'` era silenciada. */
const CLAVE_VIEJA = 'pgh.alerta.silenciada'

function leer(clave: string): string | null {
  try {
    return window.localStorage.getItem(clave)
  } catch {
    return null
  }
}

function escribir(clave: string, valor: string | null): void {
  try {
    if (valor === null) window.localStorage.removeItem(clave)
    else window.localStorage.setItem(clave, valor)
  } catch {
    // Modo incógnito o storage bloqueado: la preferencia dura la sesión.
  }
}

/**
 * ¿La persona quiere el tono? Viene encendido.
 *
 * Migra las dos claves anteriores con la regla «el silencio gana»: quien había
 * silenciado cualquiera de los dos interruptores sigue en silencio. Al revés,
 * el día que sale este cambio le empezaría a sonar el portal a alguien que
 * pidió que no sonara, y esa persona ya no vuelve a confiar en el botón.
 */
export function quiereSonido(): boolean {
  return leer(CLAVE_SONIDO) !== 'off' && leer(CLAVE_VIEJA) !== '1'
}

export function guardarSonido(on: boolean): void {
  escribir(CLAVE_SONIDO, on ? 'on' : 'off')
  escribir(CLAVE_VIEJA, null)
}

/** ¿La persona quiere el pop-up? Viene encendido: sin permiso del navegador
 *  igual no sale, y el interruptor se ve apagado hasta que lo haya. */
export function quierePopup(): boolean {
  return leer(CLAVE_POPUP) !== 'off'
}

export function guardarPopup(on: boolean): void {
  escribir(CLAVE_POPUP, on ? 'on' : 'off')
}

export function permisoDelNavegador(): PermisoPopup {
  if (typeof Notification === 'undefined') return 'sin-soporte'
  if (Notification.permission === 'granted') return 'concedido'
  if (Notification.permission === 'denied') return 'bloqueado'
  return 'pedible'
}

// ── El tono ───────────────────────────────────────────────────────────────

let audio: AudioContext | null = null

function contexto(): AudioContext | null {
  try {
    if (!audio) {
      const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return null
      audio = new Ctor()
    }
    return audio
  } catch {
    return null
  }
}

/**
 * Despierta el audio. **Se llama desde un gesto de la persona**: el `resume()`
 * dentro del gesto es lo que levanta el bloqueo, y un contexto `suspended` no
 * avisa que no sonó — devuelve una promesa rechazada que nadie mira.
 */
export async function desbloquearSonido(): Promise<boolean> {
  const ac = contexto()
  if (!ac) return false
  try {
    if (ac.state === 'suspended') await ac.resume()
  } catch {
    return false
  }
  return ac.state === 'running'
}

/** ¿El navegador ya dejó sonar? */
export function haySonido(): boolean {
  return !!audio && audio.state === 'running'
}

/**
 * El aviso es una MARIMBA: dos golpes, Sol5 y Do6, el segundo 130 ms después.
 *
 * Lo eligió Rubén el 07-oct-2026 entre seis tonos que se escucharon lado a
 * lado. El del 06-oct —un arpegio Do–Mi–Sol una octava más arriba, con la
 * octava y la quinta como armónicos— se oía, pero molestaba: llegaba casi a
 * 5 kHz, la zona donde el oído es más sensible y que se percibe como chillido,
 * y eran tres notas a −12,8 dBFS de RMS para algo que suena muchas veces por
 * hora. La marimba se queda en el registro medio (784 y 1046 Hz) y su brillo
 * es un golpe de 70 ms, no una cola: se distingue del ruido de la oficina sin
 * taladrar.
 */
const NOTAS = [
  { hz: 784.0, en: 0 },
  { hz: 1046.5, en: 0.13 },
]

/** Lo que dura cada nota: el cuerpo de madera, que se apaga solo. */
const DURA_NOTA = 0.45

/** El cuerpo de la nota: la fundamental, con ataque de 4 ms. */
const CUERPO = 0.85

/**
 * El golpe del mazo: el cuarto armónico —el que caracteriza a la marimba—,
 * fuerte al principio y apagado a los 70 ms. Es lo que hace que la nota se
 * oiga «tocada» y no como un pitido; sin él, dos senos medios se pierden.
 */
const GOLPE = { mult: 4, nivel: 0.35, dura: 0.07 }

/**
 * El volumen del aviso entero. Medido el 07-oct-2026 renderizando ESTE código
 * con un `OfflineAudioContext` a 48 kHz, contra los tonos que reemplaza:
 *
 *                              pico        RMS (300 ms)   audible
 *   marimba (0,51)             −5,0 dBFS   −18,0 dBFS     0,43 s
 *   arpegio del 06-oct         −1,7 dBFS   −12,8 dBFS     0,60 s
 *   campana original           −16,0 dBFS  −31,2 dBFS     0,23 s
 *
 * El objetivo fue −18 dBFS de RMS, el nivel con que se escuchó al elegirla:
 * unos 5 dB por debajo del arpegio que molestaba y 13 dB por encima del
 * original que no se oía. Con 1,0 picaba a +0,9 dBFS, o sea que recortaba.
 * Para cambiarlo, este número, y volver a medir: el volumen del tono no se pone
 * a ojo (el primer arpegio, «a ojo», salió 8 dB más bajo de lo calculado).
 */
export const VOLUMEN = 0.51

/** Cuánto dura el aviso entero, en segundos. */
export const DURACION_AVISO = NOTAS[NOTAS.length - 1].en + DURA_NOTA + 0.03

/** Una envolvente: sube en `ataque` segundos hasta `pico` y se apaga
 *  exponencialmente —sin rampa, cortar una onda a mitad de ciclo hace un clic—. */
function envolvente(ac: BaseAudioContext, inicio: number, pico: number, ataque: number, dura: number): GainNode {
  const g = ac.createGain()
  g.gain.setValueAtTime(0.0001, inicio)
  g.gain.exponentialRampToValueAtTime(pico, inicio + ataque)
  g.gain.exponentialRampToValueAtTime(0.0001, inicio + dura)
  return g
}

function seno(ac: BaseAudioContext, hz: number, inicio: number, dura: number, destino: AudioNode): void {
  const osc = ac.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = hz
  osc.connect(destino)
  osc.start(inicio)
  osc.stop(inicio + dura + 0.03)
}

/**
 * Dibuja el aviso sobre cualquier contexto de audio, desde `t0`.
 *
 * Recibe el contexto en vez de crearlo para que el MISMO código suene en la
 * página y se mida en un `OfflineAudioContext`: un tono que se prueba con otra
 * copia no prueba nada.
 */
export function tocarAviso(ac: BaseAudioContext, destino: AudioNode, t0: number): void {
  const salida = ac.createGain()
  salida.gain.value = VOLUMEN
  salida.connect(destino)
  for (const nota of NOTAS) {
    const inicio = t0 + nota.en
    const cuerpo = envolvente(ac, inicio, CUERPO, 0.004, DURA_NOTA)
    cuerpo.connect(salida)
    seno(ac, nota.hz, inicio, DURA_NOTA, cuerpo)
    const golpe = envolvente(ac, inicio, GOLPE.nivel, 0.002, GOLPE.dura)
    golpe.connect(salida)
    seno(ac, nota.hz * GOLPE.mult, inicio, GOLPE.dura, golpe)
  }
}

/** El aviso, en la página. No mira la preferencia —eso lo decide quien llama,
 *  porque «Probar sonido» suena aunque esté apagado— y no hace ruido si el
 *  navegador todavía no dejó sonar. */
export function sonar(): void {
  const ac = contexto()
  if (!ac) return
  try {
    if (ac.state === 'suspended') void ac.resume()
    tocarAviso(ac, ac.destination, ac.currentTime + 0.01)
  } catch {
    // Un aviso que no suena no puede tumbar el sondeo.
  }
}

/**
 * Engancha el primer gesto de la persona para despertar el audio. Se llama una
 * vez al montar el portal; los listeners se quitan solos.
 *
 * `pointerdown` y no `click`: llega antes, así el audio ya está despierto si
 * el mismo clic abre una conversación. `keydown` cubre a quien usa el teclado.
 */
export function prepararAudio(alDespertar?: () => void): () => void {
  const quitar = () => {
    window.removeEventListener('pointerdown', despertar)
    window.removeEventListener('keydown', despertar)
  }
  const despertar = () => {
    quitar()
    void desbloquearSonido().then(() => alDespertar?.())
  }
  window.addEventListener('pointerdown', despertar)
  window.addEventListener('keydown', despertar)
  return quitar
}

// ── El pop-up ─────────────────────────────────────────────────────────────
//
// Sale por un service worker (`public/sw.js`) porque es la única forma de que
// tenga un botón: `new Notification` no acepta `actions`. El botón es lo que
// abre la conversación, y en Windows también lo que deja al aviso quedarse en
// pantalla. Si el service worker no registra, sale igual por el camino de
// antes, sin botón: un aviso sin botón es peor; ninguno es mucho peor.

let registro: Promise<ServiceWorkerRegistration | null> | null = null

/**
 * Registra el service worker. Idempotente. No espera a
 * `navigator.serviceWorker.ready`, que no se rechaza nunca: uno que no llegue
 * a instalarse dejaría esperando para siempre a todos los avisos.
 */
export function prepararPopup(): Promise<ServiceWorkerRegistration | null> {
  if (!registro) {
    registro = (async () => {
      try {
        if (!('serviceWorker' in navigator)) return null
        return await navigator.serviceWorker.register('/sw.js')
      } catch {
        return null
      }
    })()
  }
  return registro
}

/**
 * Pide el permiso del pop-up. **Solo desde un clic**, nunca sola.
 */
export async function pedirPopup(): Promise<boolean> {
  try {
    if (typeof Notification === 'undefined') return false
    if (Notification.permission === 'granted') return true
    if (Notification.permission === 'denied') return false
    return (await Notification.requestPermission()) === 'granted'
  } catch {
    return false
  }
}

/**
 * ¿La persona está mirando el portal? Las dos cosas y no una: con el portal en
 * un segundo monitor y escribiendo en otra aplicación, `visibilityState` dice
 * `visible` y nadie lo está mirando.
 */
export function portalALaVista(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus()
}

/** El ícono del pop-up, en URL absoluta: Windows lo baja fuera de la página. */
export function iconoDelAviso(): string {
  return new URL(ICONO_DEL_AVISO, window.location.origin).href
}

/**
 * Muestra el pop-up. Con service worker, tocarlo lo maneja `sw.js` —trae el
 * portal al frente y le pide abrir la conversación—; sin él, `alAbrir`.
 */
export function avisar(titulo: string, opciones: OpcionesDelPopup, alAbrir?: () => void): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  void (async () => {
    try {
      const reg = await (registro ?? Promise.resolve(null))
      if (reg?.active) {
        await reg.showNotification(titulo, opciones as NotificationOptions)
        return
      }
    } catch {
      // Cae al camino de repuesto.
    }
    try {
      const n = new Notification(titulo, sinBotones(opciones) as NotificationOptions)
      n.onclick = () => {
        window.focus()
        n.close()
        alAbrir?.()
      }
    } catch {
      // Igual que el sonido: si el navegador lo rechaza, no pasa nada más.
    }
  })()
}

// ── El reloj del sondeo, también con la pestaña de fondo ──────────────────

/**
 * Llama a `fn` cada `ms`, aunque la pestaña esté oculta.
 *
 * Chrome espacia los `setInterval` de una pestaña oculta y, pasados cinco
 * minutos sin sonido, los lleva a UNO POR MINUTO («intensive throttling»). El
 * asesor casi nunca tiene el portal adelante, así que el sondeo que hace sonar
 * el aviso corría una vez por minuto justo cuando más importa: el cliente
 * escribía y el sonido llegaba hasta 60 s después. Los timers de un Worker no
 * tienen ese recorte, y el mensaje que manda a la página tampoco: el Worker
 * hace de reloj y la página hace el pedido.
 *
 * Sin Worker —o si no arranca—, el `setInterval` de siempre.
 */
export function relojDeFondo(ms: number, fn: () => void): () => void {
  try {
    const codigo = `setInterval(function(){postMessage(0)}, ${Math.max(1000, ms | 0)})`
    const url = URL.createObjectURL(new Blob([codigo], { type: 'text/javascript' }))
    const w = new Worker(url)
    w.onmessage = () => fn()
    return () => {
      w.terminate()
      URL.revokeObjectURL(url)
    }
  } catch {
    const id = window.setInterval(fn, ms)
    return () => window.clearInterval(id)
  }
}
